if (!customElements.get('search-drawer')) {
    class TG_SearchDrawerTag extends HTMLElement {
      constructor() {
        super();
        this._searchRequestId = 0;
      }
  
      connectedCallback() {
        this._disconnected = false;
        this.documentDelegate = new themegoal.libs.Delegate(document.body);
  
        this.sectionId = this.getAttribute('data-section-id');
        this.searchUrl = this.getAttribute('data-search-url') || '/search';
        this.searchInputElement = this.querySelector('[name="q"]');
        this.searchDrawerResults = this.querySelector('.SearchDrawer__Results');
        this.searchResultsElement = this.querySelector('[data-predictive-search-drawer-results]');
        this.searchStatusElement = this.querySelector('[data-predictive-search-drawer-status]');
        this.searchSummaryElement = this.querySelector('[data-predictive-search-drawer-summary]');
  
        this._bindEventsListeners();
      }
  
      disconnectedCallback(){
        this._disconnected = true;
        if(this._debouncedInput)this._debouncedInput.cancel();
        this._cancelRequest();
        this._unlockScroll();
        document.documentElement.classList.remove('SearchDrawerOpen');
        if(this.searchInputElement)this.searchInputElement.removeEventListener('input', this._onInputListener);
        if(this._onTabKeyDown)this.removeEventListener('keydown', this._onTabKeyDown, true);
        if(this.documentDelegate)this.documentDelegate.destroy();
        if(this._onTriggerPointerDown)document.removeEventListener('pointerdown', this._onTriggerPointerDown, true);
      }

      _bindEventsListeners() {
        let _this = this;
        if (!this._drawerEventsBound) {
        // "show" fires before the Show class (and its CSS transition) is
        // applied, so re-measuring here guarantees the drawer opens using the
        // header's CURRENT height rather than whatever --tg-header-height was
        // last set to on page load. The header/announcement bar heights are
        // otherwise only measured once, on initial connectedCallback - if
        // anything changes their real height afterwards (a resize, the theme
        // editor re-rendering a section while you edit it, dynamic content),
        // that CSS var goes stale and the drawer opens at the wrong `top`,
        // covering the header or leaving a gap above it.
        this.addEventListener('show.tg.Drawer', event => {
          // Lock scroll FIRST, on the earliest open event, so the page is
          // frozen at its current position before the drawer's show sequence
          // (backdrop, focus trap) can nudge it - otherwise that shift shows
          // through below the drawer since the backdrop is transparent.
          _this._lockScroll();
          // The sticky header hides itself on scroll-down by adding
          // `rp-nav-hidden` (translateY off-screen). The drawer is fixed and
          // opens right below the header's normal sticky position, so if the
          // header is currently hidden the drawer would appear over an empty
          // header slot. Reveal it (keeping it sticky/in-flow, no reflow) so
          // the drawer always lands directly beneath the visible header.
          _this._revealHeader();
          _this._syncHeaderHeight();
          // Lift the header above the drawer + its (transparent) backdrop so the
          // nav links and megamenu stay clickable/hoverable while search is open.
          // Scoped to the search drawer (Drawer--top opens BELOW the header), so
          // the full-height cart/menu drawers still cover the header as before.
          document.documentElement.classList.add('SearchDrawerOpen');
        });
        this.addEventListener('shown.tg.Drawer', event => {
          // preventScroll: focusing the input otherwise makes the browser
          // scroll it into view, nudging the page and knocking the pinned
          // header out of alignment.
          _this.searchInputElement.focus({ preventScroll: true });
          if (_this._needsSearch && _this.searchInputElement.value.trim()) {
            _this._onInput({ target: _this.searchInputElement });
          }
        });
        this.addEventListener('hidden.tg.Drawer', event => {
          if (_this._debouncedInput) _this._debouncedInput.cancel();
          _this._cancelRequest();
          _this._unlockScroll();
          document.documentElement.classList.remove('SearchDrawerOpen');
        });
        this._drawerEventsBound = true;
        }

        this._debouncedInput = this._debounce(this._onInput.bind(this), 250);
        this._onInputListener = event => {
          this.lastInputValue = event.target.value.trim();
          this._cancelRequest();
          this._needsSearch = !!this.lastInputValue;
          if (!this.lastInputValue) {
            this._debouncedInput.cancel();
            this._resetSearch();
          } else this._debouncedInput(event);
        };
        this.searchInputElement.addEventListener('input', this._onInputListener);
        this._onTabKeyDown = this._trapTab.bind(this);
        this.addEventListener('keydown', this._onTabKeyDown, true);
        this.documentDelegate.on('search:close', this._closeSearch.bind(this));

        // The search triggers are <a> links inside the sticky header. When the
        // header is scrolled out of view and you click one, the browser scrolls
        // the page to bring that focused anchor back into view - so by the time
        // the drawer opens the page has already jumped (to ~the anchor's offset)
        // and we'd save/restore that wrong position. Capture scrollY on
        // pointerdown, which fires BEFORE the focus-induced scroll, so we lock
        // and later restore the customer's real position.
        this._onTriggerPointerDown = function (e) {
          var t = e.target && e.target.closest ? e.target.closest('[data-tg-target="#SearchDrawer"]') : null;
          if (t) _this._pendingScrollY = window.scrollY || window.pageYOffset || 0;
        };
        document.addEventListener('pointerdown', this._onTriggerPointerDown, true);
      }

      // Reveal the sticky header so the drawer opens directly below it.
      // rp-nav-hidden is the class the header's own scroll script uses to
      // slide the nav off-screen on scroll-down; removing it (+ the animation
      // class) puts the header back at its normal sticky position (top:32).
      // We deliberately do NOT change the header to position:fixed here: the
      // header is position:sticky and in normal flow, so pinning it fixed would
      // remove its flow slot and reflow the page content up by the header's
      // height - the "page jumps up a bit" on open. Leaving it sticky keeps the
      // layout stable (no movement) while still showing the header.
      _revealHeader() {
        var wrap = document.querySelector('.shopify-section--header');
        if (!wrap) return;
        wrap.classList.remove('rp-nav-hidden');
        wrap.classList.remove('rp-nav-animated');
      }

      _syncHeaderHeight() {
        var header = document.getElementById('Header');
        var announcementBar = document.getElementById('AnnouncementBar');
        if (header) {
          document.documentElement.style.setProperty('--tg-header-height', header.offsetHeight + 'px');
        }
        if (announcementBar) {
          document.documentElement.style.setProperty('--tg-announcement-bar-height', announcementBar.offsetHeight + 'px');
        }
      }

      // This theme scrolls on <html> and the vertical scrollbar lives there.
      // The old lock set overflow:hidden on <html> (which REMOVED the scrollbar)
      // and added padding-right to compensate (which opened a ~15px strip on the
      // right). Instead we leave the scrollbar exactly as-is — visible, no layout
      // shift — and make it INERT: block scroll input that would move the page
      // behind the drawer, while still letting the drawer scroll its own results,
      // and snap any residual page scroll (e.g. dragging the scrollbar thumb) back
      // to the saved position.
      _lockScroll() {
        if (this._scrollLocked) return;
        this._scrollLocked = true;
        var _this = this;
        // Prefer the position captured at pointerdown (before the trigger anchor's
        // focus-scroll moved the page); fall back to the current position.
        this._savedScrollY = this._pendingScrollY != null ? this._pendingScrollY : (window.scrollY || window.pageYOffset || 0);
        this._pendingScrollY = null;

        // Block wheel/touch that would scroll the BACKGROUND, but allow the drawer
        // to scroll its own body (events whose target is inside the drawer).
        this._blockBgScroll = function (e) {
          if (e.target && e.target.closest && e.target.closest('#SearchDrawer')) return;
          e.preventDefault();
        };
        // Catch-all for scroll that isn't wheel/touch — dragging the scrollbar
        // thumb, keyboard, programmatic nudges from the open sequence: the
        // scrollbar may move but the page snaps straight back to where it was.
        this._pinScroll = function () {
          if (_this._savedScrollY != null && (window.scrollY || window.pageYOffset || 0) !== _this._savedScrollY) {
            window.scrollTo(0, _this._savedScrollY);
          }
        };
        window.addEventListener('wheel', this._blockBgScroll, { passive: false });
        window.addEventListener('touchmove', this._blockBgScroll, { passive: false });
        window.addEventListener('scroll', this._pinScroll, { passive: true });
      }

      _unlockScroll() {
        if (!this._scrollLocked) return;
        this._scrollLocked = false;
        if (this._blockBgScroll) {
          window.removeEventListener('wheel', this._blockBgScroll);
          window.removeEventListener('touchmove', this._blockBgScroll);
          this._blockBgScroll = null;
        }
        if (this._pinScroll) {
          window.removeEventListener('scroll', this._pinScroll);
          this._pinScroll = null;
        }
        // The page never moved (it was pinned), so there is no position to
        // restore and no bounce-to-top to undo.
        this._savedScrollY = null;
      }
  
      _closeSearch(){
        let searchDrawerElement = document.getElementById('SearchDrawer')
        let searchDrawer = new themegoal.components.Drawer(searchDrawerElement);
        searchDrawer.hide();
      }
    
      _trapTab(event) {
        if (event.key !== 'Tab' || !this.classList.contains('Show')) return;
        // Results and the native campaign disclosure change the visible tab
        // order after opening. Resolve its boundaries at each keyboard action.
        var controls = Array.from(this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')).filter(function (control) {
          var closedDisclosure = control.closest('details:not([open])');
          return control.getClientRects().length > 0 && !control.closest('[hidden], [inert], [aria-hidden="true"]') && (!closedDisclosure || closedDisclosure.querySelector('summary').contains(control));
        });
        if (!controls.length) return;
        var first = controls[0];
        var last = controls[controls.length - 1];
        var target = document.activeElement;
        if ((!event.shiftKey && target === last) || (event.shiftKey && (target === first || target === this))) {
          event.preventDefault();
          event.stopPropagation();
          (event.shiftKey ? last : first).focus({ preventScroll: true });
        }
      }

      _onInput(event) {
        let _this = this;
        if (!this.searchResultsElement || this._disconnected) return;
        this._cancelRequest();
    
        if (event.keyCode === 13) {
          return;
        }
    
        this.lastInputValue = event.target.value.trim();
    
        if (this.lastInputValue === '') {
          this._resetSearch();
          return;
        }

        this.classList.add('is-searching');
        this._requestController = new AbortController();
        var requestId = this._searchRequestId;
        var queryOptions = { method: 'GET', credentials: 'same-origin', signal: this._requestController.signal };
        var term = this.lastInputValue;
        var url = this.searchUrl
          + '?view=suggest&type=product&options%5Bprefix%5D=last&q='
          + encodeURIComponent(term);
    
        this._setLoading(true);
        this.searchSummaryElement.textContent = 'Searching…';
        this.searchDrawerResults.setAttribute('aria-hidden', 'false');
    
        var discoveryRequest = Promise.resolve('');
        if (document.body.classList.contains('figma-v2')) {
          var discoveryUrl = this.getAttribute('data-discovery-url')
            + '?q=' + encodeURIComponent(term)
            + '&resources[type]=collection,page,article&resources[limit]=4&resources[limit_scope]=each&section_id=v2-search-overlay-discovery';
          discoveryRequest = fetch(discoveryUrl, queryOptions).then(function (response) {
            if (!response.ok) return '';
            return response.text();
          }).catch(function () { return ''; });
        }
        Promise.all([
          fetch(window.repubblicaDiscovery ? window.repubblicaDiscovery.requestUrl(url, true) : url, queryOptions).then(function (response) {
            if (!response.ok) throw new Error('Search unavailable');
            return response.text();
          }),
          discoveryRequest
        ])
          .then(function (responses) {
            if (_this._searchRequestId !== requestId || _this._disconnected) return;
            var fragment = new DOMParser().parseFromString(responses[0], 'text/html');
            var results = fragment.querySelector('[data-search-drawer-results-fragment]');
            var summary = fragment.querySelector('[data-search-drawer-summary-fragment]');

            _this.searchResultsElement.innerHTML = results ? results.innerHTML : '';
            _this.searchSummaryElement.innerHTML = summary ? summary.innerHTML : '';
            _this._needsSearch = false;
            if (responses[1]) {
              var discovery = new DOMParser().parseFromString(responses[1], 'text/html').querySelector('[data-v2-search-discovery-fragment]');
              if (discovery) _this.searchResultsElement.appendChild(discovery);
            }
            document.dispatchEvent(new CustomEvent('repubblica:sample_cards_loaded'));
          })
          .catch(function () {
            if (_this._searchRequestId === requestId && !_this._disconnected) {
              _this.searchResultsElement.textContent = 'Search is temporarily unavailable. Please try again or press Enter to view all results.';
              _this.searchSummaryElement.innerHTML = '';
            }
          })
          .then(function () {
            if (_this._searchRequestId !== requestId || _this._disconnected) return;
            _this._setLoading(false);
            _this._requestController = null;
          });
      }

      _setLoading(loading) {
        if (this._loading !== loading) {
          if (loading || this._loading) document.dispatchEvent(new CustomEvent('theme:loading:' + (loading ? 'start' : 'end')));
          this._loading = loading;
        }
        if (this.searchStatusElement) this.searchStatusElement.style.display = loading ? 'block' : 'none';
        if (this.searchResultsElement) this.searchResultsElement.setAttribute('aria-busy', String(loading));
      }

      _cancelRequest() {
        this._searchRequestId += 1;
        if (this._loading) this._needsSearch = true;
        if (this._requestController) this._requestController.abort();
        this._requestController = null;
        this._setLoading(false);
      }

      _resetSearch() {
        this._cancelRequest();
        this._needsSearch = false;
        this.classList.remove('is-searching');
        this.searchResultsElement.innerHTML = '';
        this.searchSummaryElement.innerHTML = '';
        this.searchDrawerResults.setAttribute('aria-hidden', 'true');
      }
    
      _debounce(fn, delay) {
        var _this = this;
    
        var timer = null;
    
        var debounced = function () {
            for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
                args[_key] = arguments[_key];
            }
        
            clearTimeout(timer);
        
            timer = setTimeout(function () {
                fn.apply(_this, args);
            }, delay);
        };
        debounced.cancel = function () { clearTimeout(timer); };
        return debounced;
      }
      
    }
  
    customElements.define('search-drawer', TG_SearchDrawerTag);
  }
